import { GameScene } from '@Core/index';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ISpinOutcome } from '@GFTypes/GameStoreTypes';
import { Container, Point } from 'pixi.js';
import { ExpandingSymbol } from '../ExpandingSymbol';
import { EExpandingSymbolAnimationModifiers, EExpandingSymbolAnimations } from '../ExpandingSymbolAnimations';
import { IPositionOnReels, ISymbolInfo, SymbolExpansionStrategy } from '../ExpandingSymbolFeature.types';

export class Symbol360ExpansionStrategy implements SymbolExpansionStrategy {
    public symbolIds: number[] = [];

    constructor(
        private readonly scene: GameScene,
        private readonly topLayerContainer: Container,
        private readonly isVisibleAfterExpanding: boolean = false
    ) {}

    public getWinningExpandingSymbols(outcome: ISpinOutcome): ISymbolInfo[] {
        const winningExpandingSymbols: ISymbolInfo[] = [];
        const visibleSymbolsPerStep = outcome.steps.map((step) => step.finalSymbols.map((reel) => reel.slice(1, -1)));
        visibleSymbolsPerStep.at(0)?.forEach((reel, reelIndex) => {
            for (const [visibleSymbolIndex, symbolId] of reel.entries()) {
                const expandingSymbolId = this.symbolIds.find((id) => id === symbolId);
                if (!expandingSymbolId) continue;
                if (this.isExpandingSymbolWinning(expandingSymbolId, reelIndex, visibleSymbolIndex, visibleSymbolsPerStep.at(-1) ?? [])) {
                    winningExpandingSymbols.push({ positionOnReel: { reelIndex, visibleSymbolIndex }, symbolId });
                }
            }
        });
        return winningExpandingSymbols;
    }

    public getInitialExpandingSymbols(loopWinAnimation = false, showOnlyHighestMultiplier = false): ExpandingSymbol[] {
        const expandingSymbols: ExpandingSymbol[] = [];

        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfVisibleSymbolsPerReel = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);

        this.symbolIds.forEach((id) => {
            for (let reelIndex = 0; reelIndex < numberOfReels; reelIndex++) {
                for (let visibleSymbolIndex = 0; visibleSymbolIndex < numberOfVisibleSymbolsPerReel; visibleSymbolIndex++) {
                    const originPosition: IPositionOnReels = { reelIndex, visibleSymbolIndex };
                    const coveredPositions: IPositionOnReels[] = [];

                    for (let reelOffset = -1; reelOffset <= 1; reelOffset++) {
                        for (let symbolOffset = -1; symbolOffset <= 1; symbolOffset++) {
                            const neighborReel = reelIndex + reelOffset;
                            const neighborIndex = visibleSymbolIndex + symbolOffset;

                            if (neighborReel < 0 || neighborReel >= numberOfReels || neighborIndex < 0 || neighborIndex >= numberOfVisibleSymbolsPerReel) {
                                continue;
                            }

                            coveredPositions.push({
                                reelIndex: neighborReel,
                                visibleSymbolIndex: neighborIndex
                            });
                        }
                    }

                    expandingSymbols.push(
                        new ExpandingSymbol(
                            id,
                            [originPosition],
                            coveredPositions,
                            showOnlyHighestMultiplier,
                            this.scene,
                            this.topLayerContainer,
                            this,
                            this.isVisibleAfterExpanding
                        )
                    );
                }
            }
        });
        return expandingSymbols;
    }

    public getDimensions({ coveredPositions }: ExpandingSymbol): { width: number; height: number } {
        const reelIndices = coveredPositions.map((p) => p.reelIndex);
        const symbolIndices = coveredPositions.map((p) => p.visibleSymbolIndex);
        const width = Math.max(...reelIndices) - Math.min(...reelIndices) + 1;
        const height = Math.max(...symbolIndices) - Math.min(...symbolIndices) + 1;
        return { width, height };
    }

    public getExpandingAnimationName(expandingSymbol: ExpandingSymbol): string {
        const expandingAnimation = this.getAnimationVariant(EExpandingSymbolAnimations.SYMBOL_EXPANDING, expandingSymbol);
        return expandingAnimation;
    }

    public getSpinePosition({ coveredPositions }: ExpandingSymbol, visibleSymbols: ReelSymbol[][]): Point {
        const topLeft = coveredPositions.reduce((prev, curr) => {
            if (curr.reelIndex < prev.reelIndex) return curr;
            if (curr.reelIndex === prev.reelIndex && curr.visibleSymbolIndex < prev.visibleSymbolIndex) return curr;
            return prev;
        }, coveredPositions[0]);
        const symbol = visibleSymbols[topLeft.reelIndex][topLeft.visibleSymbolIndex];
        const x = symbol.x - symbol.width / 2;
        const y = symbol.y - symbol.height / 2;
        return new Point(x, y);
    }

    private isExpandingSymbolWinning(expandingSymbolId: number, reelIndex: number, visibleSymbolIndex: number, visibleSymbolsLastStep: number[][]): boolean {
        const numberOfReels = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfSymbols = this.scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);

        for (let reelOffset = -1; reelOffset <= 1; reelOffset++) {
            for (let symbolOffset = -1; symbolOffset <= 1; symbolOffset++) {
                if (this.isCenter(reelOffset, symbolOffset)) continue;

                const neighborReel = reelIndex + reelOffset;
                const neighborIndex = visibleSymbolIndex + symbolOffset;

                if (!this.isInBounds(neighborReel, neighborIndex, numberOfReels, numberOfSymbols)) continue;

                if (visibleSymbolsLastStep[neighborReel][neighborIndex] !== expandingSymbolId) return false;
            }
        }

        return true;
    }

    private isCenter(reelOffset: number, symbolOffset: number): boolean {
        return reelOffset === 0 && symbolOffset === 0;
    }

    private isInBounds(reel: number, index: number, maxReels: number, maxSymbols: number): boolean {
        return reel >= 0 && reel < maxReels && index >= 0 && index < maxSymbols;
    }

    public getAnimationVariant(animation: EExpandingSymbolAnimations, expandingSymbol: ExpandingSymbol): string {
        const multiplier = expandingSymbol.winningExpandingSymbolIndex + 1;
        const multiplierModifier = multiplier <= 1 ? undefined : EExpandingSymbolAnimationModifiers.MULTIPLIER.replace('$', multiplier.toString());
        const reelIndex = [...new Set(expandingSymbol.originPositions.map((position) => position.reelIndex + 1))][0];
        const reelModifier = EExpandingSymbolAnimationModifiers.REEL.replace('$', reelIndex.toString());
        const rowIndex = expandingSymbol.visibleRowIndex + 1;
        const rowModifier = EExpandingSymbolAnimationModifiers.ROW.replace('$', rowIndex.toString());
        const { width, height } = this.getDimensions(expandingSymbol);
        const dimensionsModifier = EExpandingSymbolAnimationModifiers.DIMENSIONS.replace('$', width.toString()).replace('$', height.toString());
        const modifiers = [multiplierModifier, reelModifier, rowModifier, dimensionsModifier].filter(Boolean) as string[];
        const animationVariant = this.getAnimation(animation, expandingSymbol, modifiers);
        return animationVariant;
    }

    public getAnimation(base: EExpandingSymbolAnimations, expandingSymbol: ExpandingSymbol, modifiers: string[] = []): string {
        const modifiersSet = new Set(modifiers);
        const variants = expandingSymbol.animationVariants.get(base);
        if (!variants) return base;

        let bestVariant: string = '';
        let bestScore = 0;

        for (const [keySet, names] of variants) {
            const matchCount = [...keySet].filter((m) => modifiersSet.has(m)).length;
            const score = keySet.size > 0 ? matchCount / keySet.size : 0;
            if (score > bestScore || (score === 0 && bestScore === -1)) {
                bestScore = score;
                bestVariant = names[0];
            }
        }
        bestVariant ||= base;
        return bestVariant;
    }
}
